import { graphql } from '~/graphql/generated/gql';

export const coinValueFragment = graphql(`
  fragment CoinValueFields on CoinValue {
    id
    coins
    earnableType
    earnableId
  }
`);

export const imageFileFragment = graphql(`
  fragment ImageFileFields on ImageFile {
    id
    name
    path
    publicUrl
    mimeType
    size
  }
`);

export const adminContentFileFragment = graphql(`
  fragment AdminContentFileFields on AdminFile {
    id
    userId
    type
    path
    originalName
    publicUrl
    size
    mimeType
    title
  }
`);

export const spotlightFragment = graphql(`
  fragment SpotlightFields on Spotlight {
    id
    enabled
    heading
    imageAlt
    image {
      ...PublicFileFields
    }
    links {
      title
      url
      description
    }
  }
`);

export const adminSpotlightFragment = graphql(`
  fragment AdminSpotlightFields on AdminSpotlight {
    id
    enabled
    heading
    imageAlt
    image {
      ...AdminContentFileFields
    }
    links {
      title
      url
      description
    }
  }
`);

export const adminSkillFragment = graphql(`
  fragment AdminSkillFields on AdminSkill {
    id
    name
    description
    slug
    iconFile {
      ...AdminContentFileFields
    }
  }
`);

export const fetchAdminLearningFaqsBySection = graphql(`
  query fetchAdminLearningFaqsBySection($first: Int, $orderBy: [AdminLearningFaqOrder!], $sections: [FaqSection]) {
    learningFaqsList(first: $first, filters: { sections: $sections }, orderBy: $orderBy) {
      data {
        ...AdminLearningFaqFields
      }
    }
  }
`);

export const fetchFaqSectionSettingsQuery = graphql(`
  query fetchFaqSectionSettings {
    faqSectionSettings {
      section
      isPublic
    }
  }
`);

export const setFaqSectionPublicMutation = graphql(`
  mutation setFaqSectionPublic($section: FaqSection!, $isPublic: Boolean!) {
    setFaqSectionPublic(section: $section, isPublic: $isPublic) {
      section
      isPublic
    }
  }
`);

export const createLearningAssetFromJsonMutation = graphql(`
  mutation createLearningAssetFromJson($json: JSON!) {
    createLearningAssetFromJson(json: $json) {
      warnings
      learningAsset {
        id
        slug
        name
      }
    }
  }
`);

export const upsertLearningAssetMutation = graphql(`
  mutation upsertLearningAsset($input: UpsertLearningAssetInput!) {
    upsertLearningAsset(input: $input) { 
      learningAsset {
        id
        name
        internalName
        templateName
        duration
        coverImage {
          ...AdminContentFileFields
        }
        partnerId
        partner {
          id
          name
        }
        coinValue {
          ...CoinValueFields
        }
        publishedContent {
          ...AdminLearningContentFields
        }
        draftContent {
          ...AdminLearningContentFields
          skills {
            ...AdminSkillFields
          }
        }
        draftContentId
        publishedContentId
        order
        slug
        meta {
          title
          description
        }
        publishedAt
        learningOutcomes
        structuredLearningOutcome {
          title
          description
        }
        indexingMetadata {
          careerCluster
          jobsList {
            id
            title
          }
          skillsList
          riasecSkills
        }
        considerationAreas {
          id
          label
        }
      }
    }
  }
`);

export const upsertIndustryMutation = graphql(`
  mutation upsertIndustry($input: UpsertLearningAssetInput!) {
    upsertLearningAsset(input: $input) {
      learningAsset {
        id
        name
        subtitle
        coverImage {
          ...AdminContentFileFields
        }
        partnerId
        partner {
          id
          name
        }
        publishedContent {
          id
            industryData {
                description
                summary
                quickFacts {
                    id
                    label
                    value
                }
            }
        }
        draftContent {
            id
            industryData {
                description
                summary
                quickFacts {
                    id
                    label
                    value
                }
            }
        }
        draftContentId
        publishedContentId
        order
        slug
        meta {
          title
          description
        }
        publishedAt
        indexingMetadata {
          careerCluster
          jobsList {
            id
            title
          }
          skillsList
          riasecSkills
        }
      }
    }
  }
`);

export const publishIndustryMutation = graphql(`
  mutation publishIndustry($input: PublishLearningAssetInput!) {
    publishLearningAsset(input: $input) {
      success
      learningAsset {
        id
        name
        slug
        publishedAt
        publishedContentId
        draftContentId
        coverImage {
          ...AdminContentFileFields
        }
        publishedContent {
          id
          industryData {
            description
            summary
            quickFacts {
              id
              label
              value
            }
          }
        }
        draftContent {
          id
          industryData {
            description
            summary
            quickFacts {
              id
              label
              value
            }
          }
        }
        meta {
          title
          description
        }
      }
    }
  }
`);

export const unpublishIndustryMutation = graphql(`
  mutation unpublishIndustry($input: UnpublishLearningAssetInput!) {
    unpublishLearningAsset(input: $input) {
      success
      learningAsset {
        id
        name
        slug
        publishedAt
        publishedContentId
        draftContentId
        coverImage {
          ...AdminContentFileFields
        }
        publishedContent {
          id
          industryData {
            description
            summary
            quickFacts {
              id
              label
              value
            }
          }
        }
        draftContent {
          id
          industryData {
            description
            summary
            quickFacts {
              id
              label
              value
            }
          }
        }
        meta {
          title
          description
        }
      }
    }
  }
`);

export const publishLearningAssetMutation = graphql(`
  mutation publishLearningAsset($input: PublishLearningAssetInput!) {
    publishLearningAsset(input: $input) {
      success
      learningAsset {
        id
        name
        internalName
        templateName
        duration
        coinValue {
          ...CoinValueFields
        }
        publishedContent {
          ...AdminLearningContentFields
        }
        draftContent {
          ...AdminLearningContentFields
        }
        draftContentId
        publishedContentId
        order
        slug
        meta {
          title
          description
        }
        publishedAt
      }
    }
  }
`);

export const unpublishLearningAssetMutation = graphql(`
  mutation unpublishLearningAsset($input: UnpublishLearningAssetInput!) {
    unpublishLearningAsset(input: $input) {
      success
      learningAsset {
        id
        name
        internalName
        templateName
        duration
        coinValue {
          ...CoinValueFields
        }
        publishedContent {
          ...AdminLearningContentFields
        }
        draftContent {
          ...AdminLearningContentFields
        }
        draftContentId
        publishedContentId
        order
        slug
        meta {
          title
          description
        }
        publishedAt
      }
    }
  }
`);

export const bulkPublishLearningAssetsMutation = graphql(`
  mutation bulkPublishLearningAssets($input: BulkActionLearningAssetsInput!) {
    bulkPublishLearningAssets(input: $input) {
      success
    }
  }
`);

export const bulkUnpublishLearningAssetsMutation = graphql(`
  mutation bulkUnpublishLearningAssets($input: BulkActionLearningAssetsInput!) {
    bulkUnpublishLearningAssets(input: $input) {
      success
    }
  }
`);

export const bulkUpdateLearningAssetCoinsMutation = graphql(`
  mutation bulkUpdateLearningAssetCoins(
    $input: BulkActionLearningAssetsInput!
  ) {
    bulkUpdateLearningAssetCoins(input: $input) {
      success
    }
  }
`);

export const getUsersList = graphql(`
  query getUsersList(
    $roleType: UsersListRoleType!
    $page: Int!
    $first: Int!
    $filters: UsersListFilters
    $orderBy: [UsersListOrderByClause!]
  ) {
    usersList(
      roleType: $roleType
      page: $page
      first: $first
      filters: $filters
      orderBy: $orderBy
    ) {
      data {
        id
        primaryEmail
        fullName
        cohortName
        latestCoinBalance
        accumulatedCoinBalance
        totalCashedOutCoins
        pendingCashOutCoins
        externalId
        hasSchoolStudentRole
        createdAt
        roles {
          id
          roleType
          school {
            id
            name
          }
        }
        globalStudentRole {
          id
          isJunk
        }
        accountRole {
          id
          firstName
          lastName
          classYear
          birthMonthYear
          state {
            id
            name
          }
          phoneNumber
        }
        referredBy {
          id
          accountRole {
            id
            firstName
            lastName
          }
          trustProfile {
            id
            tier
          }
        }
        trustProfile {
          id
          tier
          score
        }
        deletedAt
        latestProccessedCoinsRedemption {
          id
          coins
          status
          type
          processedAt
        }
      }
      paginatorInfo {
        ...PaginatorInfoFields
      }
    }
  }
`);

export const getUsersListCount = graphql(`
  query getUsersListCount($roleType: UsersListRoleType!, $filters: UsersListFilters) {
    usersListCount(roleType: $roleType, filters: $filters)
  }
`);

export const updateUserJunkStatus = graphql(`
  mutation updateUserJunkStatus($userId: ID!, $isJunk: Boolean!) {
    updateUserJunkStatus(userId: $userId, isJunk: $isJunk) {
      id
      studentRoles {
        id
        isJunk
        contextType
      }
    }
  }
`);

export const updateTeacherVerification = graphql(`
  mutation updateTeacherVerification($userId: ID!, $verified: Boolean!) {
    updateTeacherVerification(userId: $userId, verified: $verified) {
      id
      personalClassroom {
        id
        name
        verified
      }
    }
  }
`);

export const updateUserAppealVerification = graphql(`
  mutation updateUserAppealVerification($userId: ID!, $verified: Boolean!) {
    updateUserAppealVerification(userId: $userId, verified: $verified) {
      id
      appealVerifiedAt
      trustProfile {
        id
        tier
        score
      }
      trustProfileSignals {
        ...TrustSignalFields 
      }
    }
  }
`);

export const bulkUpdateUserJunkStatusForRedeemableCoins = graphql(`
  mutation bulkUpdateUserJunkStatusForRedeemableCoins($input: BulkUpdateUserJunkStatusForRedeemableCoinsInput!) {
    bulkUpdateUserJunkStatusForRedeemableCoins(input: $input) {
      success
      successCount
      failureCount
    }
  }
`);

export const cohortUserFragment = graphql(`
  fragment CohortUserFields on AdminUser {
    id
    primaryEmail
    fullName
    createdAt
    deletedAt
    hasSchoolStudentRole
    # Users-area data: the profile details and the whole role graph come back null for a viewer
    # without admin.users.view, so the hover preview and the roles column are built only for one
    # who holds it. The trust profile is rewards-owned and gated the same way.
    globalStudentRole {
      id
      isJunk
    }
    accountRole {
      id
      firstName
      lastName
      phoneNumber
      birthMonthYear
      classYear
      state {
        id
        name
      }
    }
    roles {
      id
      roleType
      school {
        id
        name
      }
    }
    trustProfile {
      id
      tier
      score
    }
  }
`);

export const channelProgressRowFragment = graphql(`
  fragment ChannelProgressRowFields on AdminChannelProgressRow {
    isChannelCompleted
    user {
      ...CohortUserFields
    }
  }
`);

export const getChannelProgressList = graphql(`
  query getChannelProgressList(
    $channelId: ID!
    $page: Int!
    $first: Int!
    $filters: ChannelProgressListFilters
    $orderBy: [UsersListOrderByClause!]
  ) {
    channelProgressList(
      channelId: $channelId
      page: $page
      first: $first
      filters: $filters
      orderBy: $orderBy
    ) {
      data {
        ...ChannelProgressRowFields
      }
      paginatorInfo {
        ...PaginatorInfoFields
      }
    }
  }
`);

export const exportData = graphql(`
  mutation exportData($input: ExportDataInput!) {
    core {
      file {
        exportData(input: $input) {
          file {
            id
            originalName
            signedUrl
          }
          success
          downloadUrl
          recordCount
          message
        }
      }
    }
  }
`);

/* Learning Asset Collection */

// Minimal nested learningAssets (id + publishedAt + coinValue) needed to compute
// published learning-asset count and totalCoinValue client-side for list cards.
export const adminLearningAssetCollectionListFieldsFragment = graphql(`
  fragment AdminLearningAssetCollectionListFields on AdminLearningAssetCollection {
    id
    name
    slug
    coverImage {
      ...AdminContentFileFields
    }
    collectionType
    order
    backgroundImageKey
    publishedAt
    learningAssets {
      id
      publishedAt
      coinValue {
        ...CoinValueFields
      }
    }
    completionCount
    categories {
      category
      order
    }
  }
`);

export const learningAssetCollectionExploreFieldsFragment = graphql(`
  fragment LearningAssetCollectionExploreFields on LearningAssetCollection {
    id
    name
    slug
    coverImage {
      ...PublicFileFields
    }
    collectionType
    order
    backgroundImageKey
    publishedAt
    categories {
      category
      order
    }
    learningAssets {
      id
      name
      slug
      duration
      publishedAt
      coverImage {
        ...PublicFileFields
      }
      coinValue {
        ...CoinValueFields
      }
      currentUserProgress {
        id
        completedAt
        awardedCoins
      }
    }
    logoImage {
      ...PublicFileFields
    }
    backgroundImage {
      ...PublicFileFields
    }
  }
`);

export const learningAssetCollectionFieldsFragment = graphql(`
  fragment LearningAssetCollectionFields on LearningAssetCollection {
    id
    name
    coverImage {
      ...PublicFileFields
    }
    order
    slug
    collectionType
    meta {
      title
      description
    }
    publishedAt
    description
    backgroundImageKey
    learningAssets {
      id
      name
      slug
      type
      duration
      publishedAt
      meta {
        description
      }
      coinValue {
        ...CoinValueFields
      }
      coverImage {
        ...PublicFileFields
      }
      currentUserProgress {
        id
        startAt
        completedAt
        awardedCoins
      }
      learningOutcomes
      structuredLearningOutcome {
        title
        description
      }
      publishedSkills {
        ...SkillFields
      }
      curriculumTags {
        ...CurriculumTagFields
      }
    }
    curriculumRollup {
      ...LessonCurriculumRollupFields
    }
    bonusCoinValue {
      ...CoinValueFields
    }
    backgroundImage {
      ...PublicFileFields
    }
    logoImage {
      ...PublicFileFields
    }
    badges {
      id
      name
      tier
      iconFile {
        ...PublicFileFields
      }
      coverImage {
        ...PublicFileFields
      }
      premiumBadgeFile {
        ...PublicFileFields
      }
      partner {
        ...PartnerReferenceFields
      }
      meta {
        disclaimer
      }
    }
    indexingMetadata {
      careerCluster
      jobsList {
        id
        title
      }
      skillsList
      riasecSkills
    }
    contentMeta {
      mentor {
        name
        imageFile {
          ...ImageFileFields
        }
        selectionDialogue
      }
      blinkyOnboardingMessage
      disclaimer
    }
    partners {
      ...PartnerReferenceFields
    }
    feedbackSurvey {
      ...FeedbackSurveyDefinitionFields
    }
    activeCampaign {
      ...LearningCampaignCollectionOverviewFields
      learningFaqs {
        ...LearningFaqFields
      }
    }
    completionCount
    categories {
      category
      order
    }
    spotlight {
      ...SpotlightFields
    }
  }
`);

export const adminLearningAssetCollectionFieldsFragment = graphql(`
  fragment AdminLearningAssetCollectionFields on AdminLearningAssetCollection {
    id
    name
    coverImage {
      ...AdminContentFileFields
    }
    order
    slug
    collectionType
    meta {
      title
      description
    }
    publishedAt
    description
    backgroundImageKey
    learningAssets {
      id
      name
      slug
      type
      internalName
      duration
      publishedAt
      meta {
        description
      }
      coinValue {
        ...CoinValueFields
      }
      coverImage {
        ...AdminContentFileFields
      }
      learningOutcomes
      structuredLearningOutcome {
        title
        description
      }
      publishedSkills {
        ...AdminSkillFields
      }
      curriculumTags {
        ...AdminCurriculumTagFields
      }
      considerationAreas {
        id
        label
      }
    }
    curriculumRollup {
      ...LessonCurriculumRollupFields
    }
    bonusCoinValue {
      ...CoinValueFields
    }
    backgroundImage {
      ...AdminContentFileFields
    }
    logoImage {
      ...AdminContentFileFields
    }
    badges {
      id
      name
      tier
      iconFile {
        ...AdminContentFileFields
      }
      coverImage {
        ...AdminContentFileFields
      }
      premiumBadgeFile {
        ...AdminContentFileFields
      }
      partner {
        ...AdminPartnerReferenceFields
      }
    }
    indexingMetadata {
      careerCluster
      jobsList {
        id
        title
      }
      skillsList
      riasecSkills
    }
    contentMeta {
      mentor {
        name
        imageFile {
          ...ImageFileFields
        }
        selectionDialogue
      }
      blinkyOnboardingMessage
      disclaimer
    }
    partners {
      ...AdminPartnerReferenceFields
    }
    authoredFeedbackSurvey {
      id
    }
    feedbackSurvey {
      ...FeedbackSurveyDefinitionFields
    }
    completionCount
    categories {
      category
      order
    }
    spotlight {
      ...AdminSpotlightFields
    }
  }
`);

export const fetchChannelList = graphql(`
  query fetchChannels(
    $page: Int!
    $first: Int!
    $filters: AdminLearningAssetCollectionFilters
    $orderBy: [AdminLearningAssetCollectionOrder!]
  ) {
    learningAssetCollectionsList(
      page: $page
      first: $first
      filters: $filters
      orderBy: $orderBy
    ) {
      data {
        ...AdminLearningAssetCollectionListFields
      }
      paginatorInfo {
        ...PaginatorInfoFields
      }
    }
  }
`);

/**
 * One channel, read by the id its view page was reached by. The details grid and the learning-asset
 * list are the whole reader, so the document selects only what those two print — the list fragment's
 * cover image and coin values are not part of it. `learningAssets` comes back in the collection
 * items' own order. `showCompletionCount` is opt-in on the filters, so the figure the grid labels
 * "Completions" is only answered when it is asked for, and narrowing to CHANNEL keeps a playlist id
 * from rendering as a channel.
 */
export const fetchChannelDetail = graphql(`
  query fetchChannelDetail($id: ID!) {
    learningAssetCollectionsList(
      page: 1
      first: 1
      filters: {
        ids: [$id]
        publishedStatuses: [PUBLISHED, UNPUBLISHED]
        collectionType: CHANNEL
        showCompletionCount: true
      }
    ) {
      data {
        id
        name
        slug
        publishedAt
        completionCount
        categories {
          category
          order
        }
        learningAssets {
          id
          name
          type
          publishedAt
        }
      }
    }
  }
`);

export const fetchLearningAssetCollectionExplore = graphql(`
  query fetchChannelsExplore(
    $page: Int!
    $first: Int!
    $filters: LearningAssetCollectionFilters
    $orderBy: [LearningAssetCollectionOrder!]
  ) {
    learningAssetCollections(
      page: $page
      first: $first
      filters: $filters
      orderBy: $orderBy
    ) {
      data {
        ...LearningAssetCollectionExploreFields
      }
      paginatorInfo {
        ...PaginatorInfoFields
      }
    }
  }
`);

export const fetchLearningAssetCollection = graphql(`
  query fetchChannel($filters: LearningAssetCollectionFilters) {
    learningAssetCollections(first: 1, filters: $filters) {
      data {
        ...LearningAssetCollectionFields
        # Query-level (not in the shared fragment) so mutation payloads reusing
        # the fragment can't write a null total into graphcache. Requires the
        # withLibraryAggregates filter to be populated.
        totalDurationMinutes
        # Viewer-scoped, so the survey screen can show the thanks-and-coins overlay for an
        # externally-answered survey on a reload; null everywhere else.
        externalFeedbackSurveyCoinsAwarded
      }
    }
  }
`);

export const fetchAdminLearningAssetCollection = graphql(`
  query fetchAdminChannel($filters: AdminLearningAssetCollectionFilters) {
    learningAssetCollectionsList(first: 1, filters: $filters) {
      data {
        ...AdminLearningAssetCollectionFields
        # Query-level (not in the shared fragment) so mutation payloads reusing
        # the fragment can't write a null total into graphcache. Requires the
        # withLibraryAggregates filter to be populated.
        totalDurationMinutes
      }
    }
  }
`);

export const upsertLearningAssetCollectionMutation = graphql(`
  mutation upsertLearningAssetCollection(
    $input: UpsertLearningAssetCollectionInput!
  ) {
    upsertLearningAssetCollection(input: $input) {
      learningAssetCollection {
        ...AdminLearningAssetCollectionFields
      }
    }
  }
`);

export const publishLearningAssetCollectionMutation = graphql(`
  mutation publishLearningAssetCollectionMutation(
    $input: PublishLearningAssetCollectionInput!
  ) {
    publishLearningAssetCollection(input: $input) {
      success
      learningAssetCollection {
        ...AdminLearningAssetCollectionFields
      }
    }
  }
`);

export const fetchPhoenixCampaignReport = graphql(`
    query fetchPhoenixCampaignReport {
        phoenixCampaignReport {
            acquisitions {
                newUsers
            }
            coinMetrics {
                uniqueUsersEarnedCoins
                uniqueUsersPaid
            }
            learningAssetCompletionMetrics {
                uniqueUsersCompleted
                totalLearningAssetCompletions
            }
            featuredChannelMetrics {
                completedAll
                completedAtLeastOne
            }
        }
    }
`);

export const fetchDatabricksCampaignReport = graphql(`
    query fetchDatabricksCampaignReport {
        databricksCampaignReport {
            landingPageViewsMetrics {
                pageViewsCount
            }
            activeUsersMetrics {
                dailyActiveUsersCount
                weeklyActiveUsersCount
                monthlyActiveUsersCount
            }
        }
    }
`);

export const unpublishLearningAssetCollection = graphql(`
  mutation unpublishLearningAssetCollection(
    $input: UnpublishLearningAssetCollectionInput!
  ) {
    unpublishLearningAssetCollection(input: $input) {
      success
      learningAssetCollection {
        ...AdminLearningAssetCollectionFields
      }
    }
  }
`);

export const bulkPublishLearningAssetCollectionsMutation = graphql(`
  mutation bulkPublishLearningAssetCollections($input: BulkActionLearningAssetCollectionsInput!) {
    bulkPublishLearningAssetCollections(input: $input) {
      success
    }
  }
`);

export const bulkUnpublishLearningAssetCollectionsMutation = graphql(`
  mutation bulkUnpublishLearningAssetCollections($input: BulkActionLearningAssetCollectionsInput!) {
    bulkUnpublishLearningAssetCollections(input: $input) {
      success
    }
  }
`);

export const bulkDeleteLearningAssetCollectionsMutation = graphql(`
  mutation bulkDeleteLearningAssetCollections($input: BulkActionLearningAssetCollectionsInput!) {
    bulkDeleteLearningAssetCollections(input: $input) {
      success
    }
  }
`);

export const upsertSpotlightMutation = graphql(`
  mutation upsertSpotlight($input: UpsertSpotlightInput!) {
    upsertSpotlight(input: $input) {
      spotlight {
        ...AdminSpotlightFields
      }
    }
  }
`);

export const deleteSpotlightMutation = graphql(`
  mutation deleteSpotlight($input: DeleteSpotlightInput!) {
    deleteSpotlight(input: $input) {
      success
    }
  }
`);

export const deleteChannelMutation = graphql(`
  mutation deleteChannel($input: DeleteLearningAssetCollectionInput!) {
    deleteLearningAssetCollection(input: $input) {
      success
    }
  }
`);

export const getUserDetail = graphql(`
  query getUserDetail($userId: ID!) {
    core {
      user {
        userDetail(userId: $userId) {
          id
          email
          firstName
          lastName
          fullName
          age
          phoneNumber
          isPhoneNumberVerified
          referrer {
            id
            firstName
            lastName
            email
          }
          isJunkUser
          hasGlobalStudentRole
          hasSchoolStudentRole
          isDeleted
          stateName
          classYear
          birthMonthYear
          latestCoinBalance
          createdAt
          externalId
          appealVerifiedAt
          personalClassroom {
            id
            name
            verified
          }
          loginAttempts {
            ...userLoginAttemptFields
          }
        }
      }
    }
  }
`);

export const getUserCoinTransactions = graphql(`
  query getUserCoinTransactions($userId: ID!, $page: Int!, $first: Int!, $filters: UserCoinsTransactionFilters) {
    core {
      user {
        findUser(id: $userId) {
          id
          coinsTransactions(first: $first, page: $page, filters: $filters) {
            data {
              id
              coins
              type
              entryReason
              balanceBefore
              balanceAfter
              notes
              createdAt
              referenceName
            }
            paginatorInfo {
              ...PaginatorInfoFields
            }
          }
        }
      }
    }
  }
`);

export const getReferrals = graphql(`
  query getReferrals($filters: ReferralFilters, $page: Int, $first: Int!, $orderBy: [ReferralOrderByClause!]) {
    referrals(filters: $filters, page: $page, first: $first, orderBy: $orderBy) {
      data {
        id
        referralCode {
          code
        }
        referrerUser {
          id
          primaryEmail
          fullName
        }
        refereeUser {
          id
          primaryEmail
          fullName
          trustProfile {
            id
            tier
          }
        }
        createdAt
        status
      }
      paginatorInfo {
        ...PaginatorInfoFields
      }
    }
  }
`);
export const fetchDailyStats = graphql(`
  query fetchDailyStats($days: Int!) {
    dailyStats(days: $days) {
      date
      totalNewUsers
      totalLearningAssetCompletions
    }
  }
`);

export const fetchCollectionsInCategory = graphql(`
  query categoryCollectionsList($category: CollectionCategory!) {
    categoryCollectionsList(category: $category) {
      ...AdminLearningAssetCollectionListFields
    }
  }
`);

export const attachCollectionToCategoryMutation = graphql(`
  mutation attachCollectionToCategory($collectionId: ID!, $category: CollectionCategory!, $order: Int) {
    attachCollectionToCategory(collectionId: $collectionId, category: $category, order: $order) {
      id
      categories {
        category
        order
      }
    }
  }
`);

export const detachCollectionFromCategoryMutation = graphql(`
  mutation detachCollectionFromCategory($collectionId: ID!, $category: CollectionCategory!) {
    detachCollectionFromCategory(collectionId: $collectionId, category: $category) {
      id
      categories {
        category
        order
      }
    }
  }
`);

export const reorderCollectionsInCategoryMutation = graphql(`
  mutation reorderCollectionsInCategory($category: CollectionCategory!, $orderedCollectionIds: [ID!]!) {
    reorderCollectionsInCategory(category: $category, orderedCollectionIds: $orderedCollectionIds) {
      id
      name
      categories {
        category
        order
      }
    }
  }
`);

export const adminLearningAssetCategoryRowFieldsFragment = graphql(`
  fragment AdminLearningAssetCategoryRowFields on AdminLearningAsset {
    id
    name
    type
    publishedAt
  }
`);

export const fetchLearningAssetsInCategory = graphql(`
  query categoryLearningAssetsList($category: LearningAssetCategory!) {
    categoryLearningAssetsList(category: $category) {
      ...AdminLearningAssetCategoryRowFields
    }
  }
`);

export const attachLearningAssetToCategoryMutation = graphql(`
  mutation attachLearningAssetToCategory($learningAssetId: ID!, $category: LearningAssetCategory!, $order: Int) {
    attachLearningAssetToCategory(learningAssetId: $learningAssetId, category: $category, order: $order) {
      ...AdminLearningAssetCategoryRowFields
    }
  }
`);

export const detachLearningAssetFromCategoryMutation = graphql(`
  mutation detachLearningAssetFromCategory($learningAssetId: ID!, $category: LearningAssetCategory!) {
    detachLearningAssetFromCategory(learningAssetId: $learningAssetId, category: $category) {
      ...AdminLearningAssetCategoryRowFields
    }
  }
`);

export const reorderLearningAssetsInCategoryMutation = graphql(`
  mutation reorderLearningAssetsInCategory($category: LearningAssetCategory!, $orderedLearningAssetIds: [ID!]!) {
    reorderLearningAssetsInCategory(category: $category, orderedLearningAssetIds: $orderedLearningAssetIds) {
      ...AdminLearningAssetCategoryRowFields
    }
  }
`);

export const searchAssetsForCategoryAdmin = graphql(`
  query searchAssetsForCategoryAdmin($keyword: String!) {
    learningAssetsList(
      page: 1
      first: 10
      filters: {
        keyword: $keyword
        types: [ARTICLE]
        publishedStatus: [PUBLISHED, UNPUBLISHED]
      }
    ) {
      data {
        ...AdminLearningAssetCategoryRowFields
      }
    }
  }
`);

export const searchLearningAssetsForSurveyAdmin = graphql(`
  query searchLearningAssetsForSurveyAdmin($keyword: String!) {
    learningAssetsList(
      page: 1
      first: 10
      filters: {
        keyword: $keyword
        publishedStatus: [PUBLISHED, UNPUBLISHED]
      }
    ) {
      data {
        id
        name
        internalName
        learningAssetCollections {
          id
          name
        }
      }
    }
  }
`);

export const searchCollectionsForCategoryAdmin = graphql(`
  query searchCollectionsForCategoryAdmin($keyword: String!, $collectionTypes: [LearningCollectionType!]!) {
    learningAssetCollectionsList(
      page: 1
      first: 10
      filters: {
        keyword: $keyword
        collectionTypes: $collectionTypes
        publishedStatuses: [PUBLISHED, UNPUBLISHED]
      }
    ) {
      data {
        ...AdminLearningAssetCollectionListFields
      }
    }
  }
`);

/**
 * Every trust-signal union member with its strongly-typed evidence (the signal-level union binds
 * signalType to its evidence type, so the FE narrows by __typename — no `as` casts). Selects the
 * full evidence including the heavy nested lists; shared by the detail view and the list-row
 * popovers, which reuse the same signal panels.
 */
export const trustSignalFieldsFragment = graphql(`
  fragment TrustSignalFields on TrustSignal {
    __typename
    ... on PhoneHygieneTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        smsPumpingRiskScore
        numberBlocked
        numberBlockedLast3Months
        twilioCheckedAt
        lineType
        phoneCountryCode
        phoneCountryMismatch
        carrierName
        carrierRiskCategory
      }
    }
    ... on LocationStabilityTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        regionUnresolved
        distinctRegionCount
        sameDayMove
        regions {
          name
          date
        }
        countryCodes
        outOfCountry
        foreignCountryCodes
        primarySignalFactor
        mixpanelCheckedAt
      }
    }
    ... on ScriptReplayCohortTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        cohortMemberCount
        excludedSameSchoolMemberCount
        similarityThreshold
        linkedAccounts {
          userId
          sequenceSimilarity
          sharesSchool
        }
        assetSequence {
          id
          name
          collectionName
        }
      }
    }
    ... on CoRegistrationBurstTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        burstSiblingCount
        excludedSameSchoolSiblingCount
        windowSeconds
        linkedAccounts {
          userId
          createdAt
          sharesSchool
        }
        subjectCreatedAt
      }
    }
    ... on PriorCashoutHistoryTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        rejectedCount
        lastRejectedAt
        processedCount
        processedCoins
        lastProcessedAt
      }
    }
    ... on AccountLifecycleTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        activeDayCount
        spanDays
        completionCount
        accountAgeDaysAtLastCompletion
        dailyCompletions {
          date
          completionCount
        }
        dailyCompletionsTruncated
      }
    }
    ... on CompletionVelocityTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        completionsPerActiveHour
        completionCount
        activeHours
        wallClockSpanHours
        sessionCount
        sessionGapMinutes
        sessionsTruncated
        sessions {
          completionCount
          startedAt
          endedAt
          activeMinutes
        }
        peakSessionCompletionsPerHour
        peakSessionCompletionCount
        peakSessionStartedAt
        peakSessionEndedAt
      }
    }
    ... on ReferralNetworkTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        networkMemberCount
        burstAccountCount
        burstWindowMinutes
        largestCreationClusterCount
        clusterWindowHours
        hasInfluencer
        truncated
        nodes {
          userId
          accountCreatedAt
          depth
          isSubject
          isInfluencer
          sharesSchool
        }
        edges {
          referrerUserId
          refereeUserId
          status
          referredAt
        }
      }
    }
    ... on PayoutMethodLegitimacyTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        westpacRedemptionCount
        processedCount
        pendingCount
        rejectedFailedCount
        lastRequestedAt
        lastProcessedAt
        trustStatus
        sharedAccount
        sharedAccountUserCount
        linkedAccounts {
          userId
        }
      }
    }
    ... on JunkStatusTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        isJunk
        markedJunkAt
        junkRoleCount
      }
    }

    ... on SharedUserDeviceTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        relatedUserCount
        schoolRatioUsage
        schoolRatioBaseline
        distinctSchoolNames
        distinctSchoolIds
        relatedUsersData {
          userId
          email
          firstName
          lastName
          isJunkUser
          schoolsMeta {
            id
            name
          }
        }
      }
    }

    ... on NameDuplicationTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        subjectNormalizedName
        nameSimilarityThreshold: similarityThreshold
        windowHours
        similarNamedCount
        windowCohortCount
        largestCreationClusterCount
        junkedSimilarCount
        withWestpacCashoutCount
        truncated
        linkedAccounts {
          userId
          name
          similarity
          createdAt
          withinWindow
          isJunk
          deleted
          westpacCashoutCount
        }
        subjectCreatedAt
      }
    }

    ... on AppealVerifiedTrustSignal {
      id
      signalType
      strength
      direction
      evidence {
        verified
        verifiedAt
        verifiedByUserId
        verifiedByName
        expiresAt
        ageDays
        band
        suppressedByJunk
      }
    }
  }
`);

export const getUserTrustDetail = graphql(`
  query getUserTrustDetail($userId: ID!) {
    core {
      user {
        findUser(id: $userId) {
          id
          trustProfile {
            id
            tier
            score
            generatedAt
          }
          trustProfileSignals {
            ...TrustSignalFields
          }
        }
      }
    }
  }
`);

export const regenerateUserTrustProfile = graphql(`
  mutation regenerateUserTrustProfile($userId: ID!) {
    regenerateUserTrustProfile(userId: $userId) {
      id
      tier
      score
      generatedAt
    }
  }
`);

export const getTrustUserSummaries = graphql(`
  query getTrustUserSummaries($ids: [ID!]!) {
    trustUserSummaries(ids: $ids) {
      id
      fullName
      primaryEmail
      createdAt
      deletedAt
      trustProfile {
        id
        tier
      }
    }
  }
`);
